import { useState, useRef, useCallback, useEffect, lazy, Suspense } from 'react';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { DashboardStats } from '@/components/layout/DashboardStats';
import { QuotationList } from '@/components/quotation/QuotationList';
const QuoteTemplate = lazy(() => import('@/components/quotation/QuoteTemplate').then(m => ({ default: m.QuoteTemplate })));
const QuoteDetailView = lazy(() => import('@/components/quotation/QuoteDetailView').then(m => ({ default: m.QuoteDetailView })));
const ProjectDetailView = lazy(() => import('@/components/project/ProjectDetailView').then(m => ({ default: m.ProjectDetailView })));
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { NewQuoteDialog } from '@/components/quotation/NewQuoteDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { AppTour } from '@/components/layout/AppTour';
// ── Heavy page-level routes — code-split for faster initial load ─────────────
const Clients      = lazy(() => import('./Clients'));
const Settings     = lazy(() => import('./Settings'));
const Invoices     = lazy(() => import('./Invoices'));
const AccountingPage = lazy(() => import('./accounting/AccountingPage'));
import { useQuotationStore } from '@/stores/quotationStore';
import { useInvoiceStore } from '@/stores/invoiceStore';
import { useIdentityStore } from '@/stores/identityStore';
import type { Quotation } from '@/types/quotation';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sun, Moon, Clock, FileText } from 'lucide-react';
import { InvoiceList } from '@/components/invoice/InvoiceList';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import { PerformanceButton } from '@/components/performance/PerformanceButton';
import { InstallPWA } from '@/components/layout/InstallPWA';
import { quotationService } from '@/services/quotationService';
import { useDesignConsultantStore } from '@/stores/designConsultantStore';
import { GlobalCommandHUD } from '@/components/layout/GlobalCommandHUD';

// ── Page-level skeleton for Suspense fallback ────────────────────────────────
function PageSkeleton({ label = '' }: { label?: string }) {
  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto animate-pulse" aria-label={label ? `Loading ${label}` : 'Loading'}>
      <div className="h-8 w-48 bg-muted rounded mb-4" />
      <div className="h-4 w-72 bg-muted/60 rounded mb-8" />
      <div className="grid grid-cols-1 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-muted/40 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

type View = 'dashboard' | 'quotations' | 'invoices' | 'clients' | 'settings' | 'quote-detail' | 'accounting';

export default function Index() {
  const [currentView, setCurrentViewState] = useState<View>(() => {
    const saved = localStorage.getItem('dpl-active-view') as View | null;
    // Only restore stable views, not quote-detail (which needs currentQuotation)
    // Views that require data to be loaded first get a safe fallback to 'quotations'
    // until the store has hydrated — prevents blank screens on cold-start navigation.
    const stableViews: View[] = ['dashboard', 'quotations', 'invoices', 'clients', 'settings', 'accounting'];
    // 'accounting' and 'clients' need their own data — defer to quotations as safe default
    // and let the user navigate there once data is ready
    const deferredViews: View[] = ['accounting'];
    if (saved && stableViews.includes(saved) && !deferredViews.includes(saved)) {
      return saved;
    }
    return 'quotations';
  });

  const setCurrentView = useCallback((view: View) => {
    setCurrentViewState(view);
    if (view !== 'quote-detail') {
      localStorage.setItem('dpl-active-view', view);
    }
  }, []);
  const [newQuoteDialogOpen, setNewQuoteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Next-Gen Project Builder Feature Flag
  const [enableNextGenBuilder, setEnableNextGenBuilder] = useState(() => {
    return localStorage.getItem('enable-nextgen-builder') === 'true';
  });

  const toggleNextGenBuilder = () => {
    const newValue = !enableNextGenBuilder;
    setEnableNextGenBuilder(newValue);
    localStorage.setItem('enable-nextgen-builder', String(newValue));
    if (newValue) {
      toast.success('Next-Gen Project Builder Enabled');
    } else {
      toast.success('Reverted to Classic Quote Editor');
    }
  };

  const {
    quotations,
    currentQuotation,
    isLoading,
    fetchQuotations,
    setCurrentQuotation,
    cloneQuotation,
    fetchQuotationById,
  } = useQuotationStore();

  const { invoices, fetchInvoices } = useInvoiceStore();
  const { fetchIdentity } = useIdentityStore();
  const { theme, setTheme } = useTheme();

  // App.tsx already fires fetchInitialData (which includes fetchQuotations + fetchInvoices)
  // on auth. In-flight deduplication in quotationService prevents double DB hits
  // if we still want a refresh when navigating back to this page.
  // Only re-fetch if the store is genuinely empty (stale-while-revalidate pattern).
  useEffect(() => {
    if (!quotations.length) fetchQuotations();
    if (!invoices.length) fetchInvoices();
    fetchIdentity();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL navigation and navigation state
  useEffect(() => {
    // Check URL first for specific view (e.g. from toast actions)
    const urlParams = new URLSearchParams(window.location.search);
    const urlView = urlParams.get('view') as View;
    const stableViews: View[] = ['dashboard', 'quotations', 'invoices', 'clients', 'settings', 'accounting'];
    
    if (urlView && stableViews.includes(urlView)) {
      setCurrentView(urlView);
      // Clean up URL without refreshing
      window.history.replaceState({}, '', '/');
      return;
    }

    const hasQuotationFromNavigation = window.sessionStorage.getItem('currentQuotationFromNavigation');
    if (currentQuotation && hasQuotationFromNavigation) {
      setCurrentView('quote-detail');
      window.sessionStorage.removeItem('currentQuotationFromNavigation');
    }
  }, [currentQuotation, setCurrentView]);

  // Guard: track which IDs we've already attempted a full fetch for.
  // This prevents an infinite loop when existing quotes have empty sections (old data structure).
  const fetchedIds = useRef(new Set<string>());
  const currentQuoteId = currentQuotation?.metadata?.quoteId;
  const currentQuoteStatus = currentQuotation?.metadata?.status;
  const currentQuoteVersion = currentQuotation?.metadata?.version;
  const currentQuoteSections = currentQuotation?.sections;

  // Auto-load full quotation data when currentQuotation is set but might be incomplete (e.g. from list view)
  useEffect(() => {
    const quoteId = currentQuoteId;
    if (!quoteId) return;

    // If quotation is already fully hydrated with items, skip re-fetching
    if (currentQuotation?.hydrationState === 'full') {
      fetchedIds.current.add(quoteId);
      return;
    }

    const sections = currentQuoteSections || [];
    const hasNoItems = sections.length === 0 || sections.every(s =>
      !s.roomGroups || s.roomGroups.length === 0 || s.roomGroups.every(g => !g.items || g.items.length === 0)
    );

    if (hasNoItems || currentQuotation?.hydrationState !== 'full') {
      if (!fetchedIds.current.has(quoteId)) {
        console.log('[Index] Quotation items missing or unhydrated, fetching full detail for:', quoteId);
        fetchedIds.current.add(quoteId);
        fetchQuotationById(quoteId);
      }
    }
  }, [currentQuoteId, currentQuotation?.hydrationState, currentQuoteSections, currentQuoteStatus, currentQuoteVersion, fetchQuotationById]);

  const handleNavigate = useCallback((path: string) => {
    const validViews: View[] = ['dashboard', 'quotations', 'invoices', 'clients', 'settings', 'accounting'];
    if (validViews.includes(path as View)) {
      setCurrentView(path as View);
      setCurrentQuotation(null);
    }
  }, [setCurrentView, setCurrentQuotation]);

  const handleSelectQuotation = useCallback((quotation: Quotation) => {
    window.sessionStorage.setItem('currentQuotationFromNavigation', 'true');
    setCurrentQuotation(quotation);
    setCurrentView('quote-detail');
  }, [setCurrentQuotation, setCurrentView]);

  const handleCloneQuotation = useCallback(async (quoteId: string) => {
    const cloned = await cloneQuotation(quoteId);
    if (cloned) {
      toast.success('Quotation cloned', {
        description: `New quote: ${cloned.metadata?.quoteNumber || 'TBD'}`,
      });
      window.sessionStorage.setItem('currentQuotationFromNavigation', 'true');
      setCurrentQuotation(cloned);
      setCurrentView('quote-detail');
    } else {
      toast.error('Failed to clone quotation', { description: 'The quote could not be saved. Please try again.' });
    }
  }, [cloneQuotation, setCurrentQuotation, setCurrentView]);

  const handleCreateQuotation = useCallback(async (
    quoteId: string,
    bdmId: string,
  ) => {
    try {
      // The RPC already set bdm_id and company_info.
      // We just need to load the fresh detail to jump into the template.
      const createdQuote = await quotationService.getQuotationById(quoteId, true);
      if (!createdQuote) {
        throw new Error('Created quotation could not be loaded');
      }

      await fetchQuotations(); // Sync the list background
      
      toast.success('Quotation created', {
        description: `Quote: ${createdQuote.metadata?.quoteNumber || 'TBD'}`,
      });
      
      window.sessionStorage.setItem('currentQuotationFromNavigation', 'true');
      setCurrentQuotation(createdQuote);
      setCurrentView('quote-detail');
    } catch (error) {
      console.error('[Index] Create quotation error:', error);
      toast.error('Failed to initialize quotation workspace');
    }
  }, [fetchQuotations, setCurrentQuotation, setCurrentView]);

  const handleBackToList = useCallback(() => {
    setCurrentQuotation(null);
    setCurrentView('quotations');
  }, [setCurrentQuotation, setCurrentView]);

  const renderContent = () => {
    // Quote Detail View
    if (currentView === 'quote-detail' && currentQuotation) {
      const isFinalized = currentQuotation.metadata?.status === 'finalized';
      return (
        <div className="p-3 sm:p-6 max-w-7xl mx-auto">
          <ErrorBoundary label={isFinalized ? "Quote Detail View" : "Quote Editor"}>
            <Suspense fallback={<PageSkeleton label={isFinalized ? "Quote Detail View" : "Quote Editor"} />}>
              {isFinalized ? (
                <QuoteDetailView quotation={currentQuotation} onBack={handleBackToList} />
              ) : enableNextGenBuilder && (currentQuotation as any).project_id ? (
                <ProjectDetailView projectId={(currentQuotation as any).project_id} onBack={handleBackToList} />
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToList}
                    className="mb-6 hover:bg-muted font-medium text-muted-foreground no-print"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> BACK TO ALL ESTIMATES
                  </Button>
                  <QuoteTemplate quotation={currentQuotation} />
                </>
              )}
            </Suspense>
          </ErrorBoundary>
        </div>
      );
    }

    if (currentView === 'invoices') {
      return <Suspense fallback={<PageSkeleton label="Invoices" />}><Invoices /></Suspense>;
    }

    if (currentView === 'clients') {
      return (
        <div className="p-3 sm:p-6 max-w-7xl mx-auto">
          <PageHeader
            title="Clients"
            description="Manage your client database and relationships"
          />
          <Suspense fallback={<PageSkeleton label="Clients" />}><Clients /></Suspense>
        </div>
      );
    }

    if (currentView === 'settings') {
      return (
        <div className="p-3 sm:p-6 max-w-7xl mx-auto">
          <PageHeader
            title="Settings"
            description="Configure application preferences and defaults"
          />
          <ErrorBoundary label="Settings">
            <Suspense fallback={<PageSkeleton label="Settings" />}>
              <Settings />
            </Suspense>
          </ErrorBoundary>
        </div>
      );
    }

    if (currentView === 'accounting') {
      return (
        <ErrorBoundary label="Accounting">
          <Suspense fallback={<PageSkeleton label="Accounting" />}>
            <AccountingPageWrapper />
          </Suspense>
        </ErrorBoundary>
      );
    }

    if (currentView === 'dashboard') {
      return (
        <div className="p-2 sm:p-4 space-y-3 sm:space-y-4 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div>
              <h1 className="text-base sm:text-lg font-semibold uppercase tracking-tight text-foreground">Dashboard</h1>
              <p className="text-[9px] sm:text-[10px] font-medium text-muted-foreground uppercase tracking-widest mt-0.5">Pipeline at a glance</p>
            </div>
          </div>
          <DashboardStats quotations={quotations} invoices={invoices} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-3 sm:space-y-4 min-h-[300px]">
              <div className="flex items-center justify-between px-2">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold uppercase tracking-tight text-slate-900 dark:text-foreground truncate">Recent Projects</h2>
                  <p className="text-[8px] sm:text-[10px] font-medium text-muted-foreground uppercase tracking-widest italic leading-none mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3 shrink-0" /> Latest engineering estimates
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigate('quotations')}
                  className="text-primary font-semibold uppercase tracking-widest text-[9px] sm:text-[10px] hover:bg-primary/5 px-2 sm:px-4 shrink-0"
                >
                  View Library
                </Button>
              </div>
              <div className="overflow-hidden rounded-lg">
                <QuotationList
                  quotations={(quotations || []).slice(0, 5)}
                  onSelect={handleSelectQuotation}
                  onClone={handleCloneQuotation}
                  onCreateNew={() => setNewQuoteDialogOpen(true)}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isLoading={isLoading}
                />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4 min-h-[300px]">
              <div className="flex items-center justify-between px-2">
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold uppercase tracking-tight text-slate-900 dark:text-foreground truncate">Recent Commercials</h2>
                  <p className="text-[8px] sm:text-[10px] font-medium text-muted-foreground uppercase tracking-widest italic leading-none mt-1 flex items-center gap-1">
                    <FileText className="h-3 w-3 shrink-0" /> Latest financial records
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigate('invoices')}
                  className="text-primary font-semibold uppercase tracking-widest text-[9px] sm:text-[10px] hover:bg-primary/5 px-2 sm:px-4 shrink-0"
                >
                  Commercials
                </Button>
              </div>
              <div className="overflow-hidden rounded-lg">
                <InvoiceList 
                  limit={4}
                  onSelect={(id) => {
                    window.sessionStorage.setItem('selectedInvoiceId', id);
                    handleNavigate('invoices');
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Quotations List View (default)
    return (
      <div className="p-3 sm:p-6 space-y-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between bg-indigo-50 border border-indigo-100 p-4 rounded-xl">
          <div>
            <h3 className="text-indigo-900 font-bold text-sm flex items-center gap-2">
              <span className="bg-indigo-600 text-white px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest">Beta</span>
              Next-Gen Project Builder
            </h3>
            <p className="text-indigo-700 text-xs mt-1">Experience the new Phase 2 Module Builder architecture.</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg shadow-sm border border-indigo-200 hover:bg-indigo-50 transition-colors">
            <input 
              type="checkbox" 
              checked={enableNextGenBuilder} 
              onChange={toggleNextGenBuilder} 
              className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4" 
            />
            <span className="text-sm font-semibold text-indigo-900">{enableNextGenBuilder ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
        <QuotationList
          quotations={quotations}
          onSelect={handleSelectQuotation}
          onClone={handleCloneQuotation}
          onCreateNew={() => setNewQuoteDialogOpen(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isLoading={isLoading}
        />
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden transition-colors duration-500 font-sans selection:bg-emerald-500/30 group/main">
      {/* Ambient Mesh Gradients */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden opacity-50 dark:opacity-40">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-blue-600/10 blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[20%] right-[10%] w-[40%] h-[40%] rounded-full bg-emerald-600/5 blur-[120px]" />
      </div>

      <AppSidebar
        currentPath={currentView === 'quote-detail' ? 'quotations' : currentView}
        onNavigate={handleNavigate}
      />

      <GlobalCommandHUD 
        onNavigate={handleNavigate}
        onCreateQuote={() => setNewQuoteDialogOpen(true)}
        setCurrentQuotation={setCurrentQuotation}
      />

      <main className="flex-1 min-w-0 overflow-auto relative z-10 scrollbar-thin scrollbar-thumb-slate-800">
        <div className="pt-16 sm:pt-0 acc-shell max-w-[1400px] mx-auto">
          {renderContent()}
        </div>
      </main>

      <NewQuoteDialog
        open={newQuoteDialogOpen}
        onOpenChange={setNewQuoteDialogOpen}
        onCreateQuote={handleCreateQuotation}
      />

      <InstallPWA />
      <AppTour />
    </div>
  );
}

/**
 * AccountingPageWrapper — resolves tenantId and userId lazily.
 * Keeps Index.tsx clean — no global state pollution.
 */
function AccountingPageWrapper() {
  const [tenantId, setTenantId] = useState('');
  const [plan, setPlan]         = useState('');
  const [userId, setUserId]     = useState('');
  const [ready, setReady]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setReady(true); return; }
        setUserId(session.user.id);
        const { getCurrentTenant } = await import('@/services/tenantService');
        const tenant = await getCurrentTenant();
        if (tenant) {
          setTenantId(tenant.id);
          setPlan(tenant.plan || 'free');
        }
      } catch (err) {
        console.error('[Index] init failed:', err);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="h-6 w-6 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return <AccountingPage tenantId={tenantId} userId={userId} plan={plan} />;
}
